import { tz } from "@date-fns/tz";
import { useDRPContext } from "@inputs/date/DateRangePicker/context";
import { rangeDateToString } from "@inputs/date/DateRangePicker/logics/date";
import type { IDRPHeaderForm } from "@inputs/date/DateRangePicker/types";
import { endOfMonth, set, startOfMonth } from "date-fns";
import { useCallback, useEffect } from "react";
import { type FieldErrorsImpl, type Resolver, useForm } from "react-hook-form";

const dateRegex = /^(\d{2})\/(\d{4})$/;
const dateFormatOptions: Intl.DateTimeFormatOptions = { month: "2-digit", year: "numeric" };

const parseDate = (value: string) => {
  const match = value.match(dateRegex);
  if (match) {
    const month = (Number(match[1]) || 1) - 1;
    const year = Number(match[2]) || 0;
    return { month, year };
  }
  return null;
};

const resolver: Resolver<IDRPHeaderForm> = (values) => {
  const start = parseDate(values.startDate);
  const end = parseDate(values.endDate);

  const errors: Partial<FieldErrorsImpl<IDRPHeaderForm>> = {};
  if (start && (start.month < 0 || start.month > 11)) {
    errors.startDate = { type: "validate", message: "La date est invalide" };
  }
  if (end && (end.month < 0 || end.month > 11)) {
    errors.endDate = { type: "validate", message: "La date est invalide" };
  }
  if (errors.startDate ?? errors.endDate) {
    return { values: {}, errors };
  }
  if (start && end && (start.year > end.year || (start.year === end.year && start.month > end.month))) {
    return {
      values: {},
      errors: {
        startDate: { type: "validate", message: "La date de début ne peut pas être supérieure à la date de fin" },
      },
    };
  }

  if (errors.startDate ?? errors.endDate) {
    return { values: {}, errors };
  }
  return { values, errors: {} };
};

export const useDRMPHeader = () => {
  const { range, handleRangeChange, error, required, timeZone } = useDRPContext();
  const { reset, control, handleSubmit } = useForm<IDRPHeaderForm>({
    defaultValues: rangeDateToString(range, dateFormatOptions, timeZone),
    resolver,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    reset(rangeDateToString(range, dateFormatOptions, timeZone));
  }, [range]);

  const submit = useCallback(
    (values: IDRPHeaderForm) => {
      const start = parseDate(values.startDate);
      const end = parseDate(values.endDate);
      const tzCtx = { in: tz(timeZone) };

      handleRangeChange({
        startDate: start ? set(startOfMonth(new Date(start.year, start.month), tzCtx), { milliseconds: 1 }) : null,
        endDate: end ? set(endOfMonth(new Date(end.year, end.month), tzCtx), { milliseconds: 998 }) : null,
      });
    },
    [handleRangeChange, timeZone]
  );

  return { control, submit: handleSubmit(submit), error, required };
};
